import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardHeader from '../components/DashboardHeader';
import EngineerProjectForm from '../components/EngineerProjectForm';
import MilestoneCompletionModal from '../components/MilestoneCompletionModal';
import { api } from '../utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Calendar, Upload, CheckCircle2, Clock, Plus, FileText, Target, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const EngineerDashboard = () => {
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [pendingProjects, setPendingProjects] = useState([]);
  const [projectMilestones, setProjectMilestones] = useState({});
  const [loading, setLoading] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const [expandedProject, setExpandedProject] = useState(null);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, projectsRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/projects')
      ]);
      
      setUser(userRes.data);
      // Active projects (assigned to this engineer)
      const activeProjects = projectsRes.data.filter(p => ['active', 'completed', 'on_hold'].includes(p.status));
      setProjects(activeProjects);
      // Projects submitted by this engineer that are pending
      setPendingProjects(projectsRes.data.filter(p => ['in_review', 'pending_approval'].includes(p.status)));
      
      // Fetch milestones for all active projects
      const milestonesMap = {};
      for (const project of activeProjects) {
        try {
          const milestoneRes = await api.get(`/projects/${project.project_id}/milestones`);
          milestonesMap[project.project_id] = milestoneRes.data;
        } catch (e) {
          milestonesMap[project.project_id] = [];
        }
      }
      setProjectMilestones(milestonesMap);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'active': { label: 'Active', className: 'bg-green-100 text-green-700' },
      'completed': { label: 'Completed', className: 'bg-blue-100 text-blue-700' },
      'on_hold': { label: 'On Hold', className: 'bg-orange-100 text-orange-700' },
      'in_review': { label: 'In Review', className: 'bg-purple-100 text-purple-700' },
      'pending_approval': { label: 'Pending', className: 'bg-amber-100 text-amber-700' }
    };
    return badges[status] || { label: status, className: 'bg-stone-100 text-stone-700' };
  };

  const getMilestoneStatusBadge = (status) => {
    const badges = {
      'pending': { label: 'Pending', className: 'bg-stone-100 text-stone-600', canComplete: true },
      'in_progress': { label: 'In Progress', className: 'bg-blue-100 text-blue-700', canComplete: true },
      'pending_review': { label: 'Under Review', className: 'bg-amber-100 text-amber-700', canComplete: false },
      'approved': { label: 'Approved', className: 'bg-green-100 text-green-700', canComplete: false },
      'rejected': { label: 'Rejected', className: 'bg-red-100 text-red-700', canComplete: true }
    };
    return badges[status] || { label: status, className: 'bg-stone-100 text-stone-700', canComplete: true };
  };

  const toggleProjectExpand = (projectId) => {
    setExpandedProject(expandedProject === projectId ? null : projectId);
  };

  const handleMarkComplete = (milestone) => {
    setSelectedMilestone(milestone);
    setShowCompletionModal(true);
  };

  const calculateProjectProgress = (projectId) => {
    const milestones = projectMilestones[projectId] || [];
    if (milestones.length === 0) return 0;
    
    let totalProgress = 0;
    for (const m of milestones) {
      if (m.status === 'approved') {
        totalProgress += m.weightage || 0;
      }
    }
    return Math.round(totalProgress);
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const engineerTypeLabel = user.engineer_type === 'design' ? 'Design Engineer' : 'Construction Engineer';

  // Calculate stats
  const totalMilestones = Object.values(projectMilestones).flat().length;
  const pendingMilestones = Object.values(projectMilestones).flat().filter(m => m.status === 'pending' || m.status === 'in_progress').length;
  const completedMilestones = Object.values(projectMilestones).flat().filter(m => m.status === 'approved').length;

  return (
    <div className="min-h-screen bg-background transition-colors duration-300" data-testid="engineer-dashboard">
      <DashboardHeader user={user} unreadCount={0} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 gap-4"
        >
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-1">
              {engineerTypeLabel} Dashboard
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base">Manage your assigned projects and milestones</p>
          </div>
          <Button 
            onClick={() => setShowNewProject(true)}
            className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground rounded-full h-12 font-semibold shadow-lg shadow-primary/20 active:scale-95 transition-all touch-target"
            data-testid="new-project-button"
          >
            <Plus className="mr-2 h-5 w-5" />
            Submit New Project
          </Button>
        </motion.div>

        {/* Quick Stats - Mobile Optimized */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          {[
            { label: 'Active Projects', value: projects.length, icon: Calendar, color: 'text-primary' },
            { label: 'Pending Review', value: pendingProjects.length, icon: FileText, color: 'text-amber-500' },
            { label: 'Milestones Due', value: pendingMilestones, icon: Target, color: 'text-orange-500' },
            { label: 'Completed', value: completedMilestones, icon: CheckCircle2, color: 'text-green-500' }
          ].map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="stat-card border-border bg-card">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-1">{stat.label}</p>
                      <p className="text-2xl sm:text-3xl font-heading font-bold text-foreground">{stat.value}</p>
                    </div>
                    <div className={`p-2 sm:p-3 rounded-xl bg-muted ${stat.color}`}>
                      <stat.icon className="h-5 w-5 sm:h-6 sm:w-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Pending Projects Section */}
        {pendingProjects.length > 0 && (
          <div className="mb-6 sm:mb-8">
            <h2 className="text-lg sm:text-xl font-heading font-semibold text-foreground mb-4">Submitted for Review</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {pendingProjects.map((project, index) => {
                const statusBadge = getStatusBadge(project.status);
                return (
                  <Card key={project.project_id} className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-foreground">{project.project_name}</h4>
                        <Badge className={statusBadge.className}>{statusBadge.label}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{project.location}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Customer: {project.customer_name}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Projects with Milestones */}
        <div>
          <h2 className="text-xl sm:text-2xl font-heading font-bold text-foreground mb-6">Your Active Projects</h2>
          
          {projects.length === 0 ? (
            <Card className="border-border bg-card">
              <CardContent className="p-12 text-center">
                <Calendar className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  No assigned projects
                </h3>
                <p className="text-muted-foreground mb-4">Submit a new project for admin review or contact your admin for assignments</p>
                <Button 
                  onClick={() => setShowNewProject(true)}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Submit New Project
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {projects.map((project, index) => {
                const statusBadge = getStatusBadge(project.status);
                const milestones = projectMilestones[project.project_id] || [];
                const isExpanded = expandedProject === project.project_id;
                const progress = calculateProjectProgress(project.project_id);
                
                return (
                  <motion.div
                    key={project.project_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="border-border bg-card overflow-hidden" data-testid={`engineer-project-card-${project.project_id}`}>
                      <CardHeader 
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => toggleProjectExpand(project.project_id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <CardTitle className="text-lg font-heading text-foreground">
                                {project.project_name}
                              </CardTitle>
                              <Badge className={`${statusBadge.className} rounded-sm text-xs`}>
                                {statusBadge.label}
                              </Badge>
                            </div>
                            <CardDescription className="text-sm">{project.location}</CardDescription>
                            <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                              <span>Customer: {project.customer_name}</span>
                              <span>•</span>
                              <span>{milestones.length} milestones</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">Progress</p>
                              <p className="text-xl font-bold text-foreground">{progress}%</p>
                            </div>
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-muted-foreground" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-muted-foreground" />
                            )}
                          </div>
                        </div>
                        <Progress value={progress} className="mt-3 h-2" />
                      </CardHeader>
                      
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <CardContent className="pt-0 pb-4 border-t border-border">
                              <h4 className="text-sm font-semibold text-foreground mb-4 mt-4">Project Milestones</h4>
                              
                              {milestones.length === 0 ? (
                                <p className="text-sm text-muted-foreground py-4">No milestones defined for this project.</p>
                              ) : (
                                <div className="space-y-3">
                                  {milestones.sort((a, b) => a.sequence - b.sequence).map((milestone) => {
                                    const mStatus = getMilestoneStatusBadge(milestone.status);
                                    return (
                                      <div 
                                        key={milestone.milestone_id} 
                                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                                      >
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className="font-medium text-foreground">{milestone.name}</span>
                                            <Badge className={`${mStatus.className} text-xs`}>
                                              {mStatus.label}
                                            </Badge>
                                          </div>
                                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                            <span>Weightage: {milestone.weightage}%</span>
                                            <span>Due: {new Date(milestone.end_date).toLocaleDateString()}</span>
                                          </div>
                                          {milestone.status === 'rejected' && milestone.admin_feedback && (
                                            <div className="mt-2 flex items-start gap-2 text-xs text-red-600">
                                              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                              <span>Rejected: {milestone.admin_feedback}</span>
                                            </div>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                          {mStatus.canComplete && (
                                            <Button
                                              size="sm"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleMarkComplete(milestone);
                                              }}
                                              className="bg-green-600 hover:bg-green-700 text-white text-xs h-8"
                                              data-testid={`complete-milestone-${milestone.milestone_id}`}
                                            >
                                              <CheckCircle2 className="w-3 h-3 mr-1" />
                                              Mark Complete
                                            </Button>
                                          )}
                                          {milestone.status === 'pending_review' && (
                                            <span className="text-xs text-amber-600 flex items-center gap-1">
                                              <Clock className="w-3 h-3" />
                                              Awaiting admin review
                                            </span>
                                          )}
                                          {milestone.status === 'approved' && (
                                            <span className="text-xs text-green-600 flex items-center gap-1">
                                              <CheckCircle2 className="w-3 h-3" />
                                              Completed
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              
                              <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                                <Link to={`/projects/${project.project_id}`} className="flex-1">
                                  <Button variant="outline" className="w-full">
                                    View Full Details
                                  </Button>
                                </Link>
                              </div>
                            </CardContent>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* New Project Form Modal */}
      <EngineerProjectForm
        open={showNewProject}
        onClose={() => setShowNewProject(false)}
        onSuccess={fetchData}
      />

      {/* Milestone Completion Modal */}
      <MilestoneCompletionModal
        open={showCompletionModal}
        onClose={() => {
          setShowCompletionModal(false);
          setSelectedMilestone(null);
        }}
        milestone={selectedMilestone}
        onSuccess={fetchData}
      />
    </div>
  );
};

export default EngineerDashboard;
